import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Alert } from 'antd';
import {
  getDoctorCharityLedger,
  getDoctorCharityLedgerSummary,
} from '../../api/medics-api';
import { formatPriceWithCurrency } from '../../utils/formatPrice';
import { formatCharityDonationStatusAsPill } from '../../utils/formatCharityDonationStatus';
import GenericTable from '../tables/GenericTable';
import Spinner from '../elements/Spinner/Spinner';

const summaryCardStyle = {
  border: '1px solid #E5E7EB',
  borderRadius: 12,
  padding: '20px 24px',
  minWidth: 200,
};

/**
 * Shows a mentor's own charitable-donation impact: the 0.5% set aside for
 * charity on every order placed through their referral. Reused by the
 * doctor's own self-service dashboard (pages/clinic-medic) and by the admin
 * doctor-detail page (pages/medics/view).
 */
const DoctorCharityEarnings = ({ doctorId }) => {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!doctorId) return;
    setLoading(true);
    const [summaryRes, ledgerRes] = await Promise.all([
      getDoctorCharityLedgerSummary(doctorId),
      getDoctorCharityLedger(doctorId, { page: 1, limit: 20 }),
    ]);
    if (!summaryRes.success || !ledgerRes.success) {
      setError(
        summaryRes.message ||
          ledgerRes.message ||
          'Greška pri učitavanju donacija.'
      );
      setSummary(null);
      setItems([]);
    } else {
      setError('');
      setSummary(summaryRes.data);
      setItems(ledgerRes.data?.items || []);
    }
    setLoading(false);
  }, [doctorId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const columns = [
    {
      key: 'orderDate',
      label: 'Datum narudžbine',
      render: (val) => (val ? dayjs(val).format('DD.MM.YYYY') : '-'),
    },
    { key: 'orderNumber', label: 'Broj narudžbine' },
    {
      key: 'orderTotal',
      label: 'Vrednost narudžbine',
      render: (val) => formatPriceWithCurrency(val || 0),
    },
    {
      key: 'amount',
      label: 'Donacija (0.5%)',
      render: (val) => formatPriceWithCurrency(val || 0),
    },
    {
      key: 'status',
      label: 'Status',
      render: (val) => formatCharityDonationStatusAsPill(val),
    },
  ];

  if (loading) {
    return <Spinner size="lg" />;
  }

  return (
    <div>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div
        style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}
      >
        <div style={summaryCardStyle}>
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
            Vrednost narudžbina preko mene
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>
            {formatPriceWithCurrency(summary?.totalOrderValue || 0)}
          </div>
        </div>
        <div style={summaryCardStyle}>
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
            Doprinos humanitarnoj organizaciji
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>
            {formatPriceWithCurrency(summary?.totalAmount || 0)}
          </div>
        </div>
        <div style={summaryCardStyle}>
          <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>
            Broj narudžbina
          </div>
          <div style={{ fontSize: 22, fontWeight: 600 }}>
            {summary?.totalOrders || 0}
          </div>
        </div>
      </div>

      <GenericTable
        data={items}
        columns={columns}
        emptyState={{
          icon: '/assets/icons/no-ads.svg',
          title: 'Nema podataka o donacijama',
        }}
      />
    </div>
  );
};

DoctorCharityEarnings.propTypes = {
  doctorId: PropTypes.string,
};

export default DoctorCharityEarnings;
